/**
 * PinnedTilesRow — до 4 закачени любими плочки в началото на dashboard-а.
 *
 * Drag-and-drop подредба (dnd-kit), запомнена в профила
 * (GET/PUT /api/preferences/dashboard-pins → user.preferences.dashboard_pins).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pin, PinOff, Plus, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { TILE_CATALOG, getTile } from "@/components/dashboard/tileCatalog";

const MAX_PINS = 4;

function SortableTile({ tile, isDark, bg, onOpen, onUnpin, editing }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tile.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative rounded-xl border ${tile.border} bg-gradient-to-br ${tile.gradient} p-3 ${
        isDark ? "hover:bg-slate-800/40" : "hover:bg-white/70"
      } transition-shadow ${isDragging ? "shadow-xl" : "hover:shadow-md"}`}
      data-testid={`pinned-tile-${tile.id}`}
    >
      <div className="flex items-start gap-2">
        <button
          {...attributes}
          {...listeners}
          className={`mt-0.5 cursor-grab active:cursor-grabbing touch-none ${isDark ? "text-slate-500" : "text-slate-400"}`}
          aria-label={bg ? "Премести" : "Reorder"}
          data-testid={`pinned-tile-handle-${tile.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <button onClick={() => onOpen(tile)} className="flex-1 min-w-0 text-left" data-testid={`pinned-tile-open-${tile.id}`}>
          <div className="flex items-center gap-1.5">
            <span className="text-lg leading-none">{tile.emoji}</span>
            <span className={`font-semibold text-sm leading-tight truncate ${isDark ? "text-slate-100" : "text-slate-900"}`}>
              {bg ? tile.title_bg : tile.title_en}
            </span>
          </div>
          <div className={`text-[10px] mt-1 leading-tight line-clamp-2 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg ? tile.sub_bg : tile.sub_en}
          </div>
        </button>
        {editing && (
          <button
            onClick={() => onUnpin(tile.id)}
            className={`shrink-0 p-1 rounded-md ${isDark ? "text-rose-300 hover:bg-rose-500/20" : "text-rose-600 hover:bg-rose-100"}`}
            aria-label={bg ? "Откачи" : "Unpin"}
            data-testid={`pinned-tile-unpin-${tile.id}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function PinnedTilesRow({ bg = true }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [pins, setPins] = useState([]);
  const [auto, setAuto] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, a] = await Promise.all([
          axios.get(`${API}/preferences/dashboard-pins`),
          axios.get(`${API}/preferences/dashboard-pins/auto`),
        ]);
        if (!alive) return;
        setPins((p.data?.pins || []).filter(getTile));
        const sug = a.data?.auto;
        setAuto(sug && getTile(sug.tile_id) ? sug : null);
      } catch { /* silent */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [API]);

  const dismissAuto = async () => {
    const tileId = auto?.tile_id;
    setAuto(null);
    try {
      await axios.post(`${API}/preferences/dashboard-pins/auto/dismiss`, { tile_id: tileId });
      toast.success(bg ? "Върнахме Вашата плочка." : "Your tile is back.");
    } catch { /* silent */ }
  };

  const keepAuto = async () => {
    const tileId = auto?.tile_id;
    setAuto(null);
    const next = pins.includes(tileId) ? pins : [...pins.slice(0, MAX_PINS - 1), tileId];
    setPins(next);
    try {
      await axios.put(`${API}/preferences/dashboard-pins`, { pins: next });
      toast.success(bg ? "Закачихме плочката за постоянно." : "Tile pinned permanently.");
    } catch {
      toast.error(bg ? "Грешка при запис на плочките." : "Failed to save tiles.");
    }
  };

  const persist = useCallback(async (next) => {
    setPins(next);
    try {
      await axios.put(`${API}/preferences/dashboard-pins`, { pins: next });
    } catch {
      toast.error(bg ? "Грешка при запис на плочките." : "Failed to save tiles.");
    }
  }, [API, bg]);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = pins.indexOf(active.id);
    const to = pins.indexOf(over.id);
    if (from < 0 || to < 0) return;
    persist(arrayMove(pins, from, to));
  };

  const togglePin = (id) => {
    if (pins.includes(id)) { persist(pins.filter((p) => p !== id)); return; }
    if (pins.length >= MAX_PINS) {
      toast.info(bg ? `Максимум ${MAX_PINS} закачени плочки — откачете една.` : `Max ${MAX_PINS} pinned tiles — unpin one first.`);
      return;
    }
    persist([...pins, id]);
  };

  if (!loaded) return null;

  const autoTile = auto ? getTile(auto.tile_id) : null;
  // При автопилот последният слот се отстъпва на предложената плочка.
  const shownPins = autoTile ? pins.slice(0, MAX_PINS - 1) : pins;
  const tiles = shownPins.map(getTile).filter(Boolean);

  return (
    <div data-testid="pinned-tiles-row">
      <div className="flex items-center justify-between gap-2 mb-2 px-1">
        <div className={`text-[11px] uppercase tracking-[0.14em] font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
          {bg ? "Закачени плочки" : "Pinned tiles"}
        </div>
        <button
          onClick={() => setEditing((v) => !v)}
          className={`text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-full border ${
            editing
              ? (isDark ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10" : "border-emerald-500/50 text-emerald-700 bg-emerald-50")
              : (isDark ? "border-slate-700 text-slate-400" : "border-gray-200 text-slate-500")
          }`}
          data-testid="pinned-tiles-edit-toggle"
        >
          {editing ? <><PinOff className="w-3 h-3" /> {bg ? "Готово" : "Done"}</>
                   : <><Pin className="w-3 h-3" /> {bg ? "Закачи / подреди" : "Pin / reorder"}</>}
        </button>
      </div>

      {tiles.length === 0 && !autoTile ? (
        <button
          onClick={() => setEditing(true)}
          className={`w-full rounded-xl border border-dashed p-3 text-left text-xs flex items-center gap-2 ${
            isDark ? "border-slate-700 text-slate-400 hover:bg-slate-800/40" : "border-gray-300 text-slate-500 hover:bg-gray-50"
          }`}
          data-testid="pinned-tiles-empty"
        >
          <Plus className="w-4 h-4" />
          {bg ? `Закачете до ${MAX_PINS} любими раздела в началото на таблото` : `Pin up to ${MAX_PINS} favourite modules to the top`}
        </button>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={shownPins} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {tiles.map((t) => (
                <SortableTile
                  key={t.id}
                  tile={t}
                  isDark={isDark}
                  bg={bg}
                  editing={editing}
                  onOpen={(tile) => navigate(tile.path)}
                  onUnpin={togglePin}
                />
              ))}
              {autoTile && (
                <div
                  className={`relative rounded-xl border-2 border-dashed ${autoTile.border} bg-gradient-to-br ${autoTile.gradient} p-3`}
                  data-testid="pinned-tile-auto"
                >
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                      {bg ? "Автопилот" : "Autopilot"}
                    </span>
                  </div>
                  <button onClick={() => navigate(autoTile.path)} className="block w-full text-left mt-1"
                    data-testid="pinned-tile-auto-open">
                    <div className="flex items-center gap-1.5">
                      <span className="text-lg leading-none">{autoTile.emoji}</span>
                      <span className={`font-semibold text-sm leading-tight truncate ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                        {bg ? autoTile.title_bg : autoTile.title_en}
                      </span>
                    </div>
                    <div className={`text-[10px] mt-1 leading-tight line-clamp-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                      {bg ? auto.reason_bg : (auto.reason_en || auto.reason_bg)}
                    </div>
                  </button>
                  <div className="flex items-center gap-1.5 mt-2">
                    <button onClick={keepAuto}
                      className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-300"
                      data-testid="pinned-tile-auto-keep">
                      {bg ? "Закачи я" : "Keep it"}
                    </button>
                    <button onClick={dismissAuto}
                      className={`text-[10px] font-semibold px-2 py-1 rounded-full ${isDark ? "bg-white/10 text-slate-300" : "bg-slate-200 text-slate-600"}`}
                      data-testid="pinned-tile-auto-restore">
                      {bg ? "Върни моята плочка" : "Restore my tile"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {editing && (
        <div className={`mt-2 p-3 rounded-xl border ${isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-200 bg-white"}`}
          data-testid="pinned-tiles-picker">
          <div className={`text-[11px] mb-2 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg
              ? `Изберете до ${MAX_PINS} раздела (${pins.length}/${MAX_PINS}). Влачете плочките, за да ги подредите.`
              : `Choose up to ${MAX_PINS} modules (${pins.length}/${MAX_PINS}). Drag the tiles to reorder.`}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {TILE_CATALOG.map((t) => {
              const on = pins.includes(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => togglePin(t.id)}
                  className={`text-[11px] font-semibold px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 transition-colors ${
                    on
                      ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                      : isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-gray-200 text-slate-600 hover:bg-gray-50"
                  }`}
                  data-testid={`pinned-tiles-pick-${t.id}`}
                >
                  <span>{t.emoji}</span>
                  {bg ? t.title_bg : t.title_en}
                  {on ? <Pin className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
